import { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ApiContext } from '../App';
import { toast } from 'react-toastify';
import ProcessBizDevBoard from '../components/processBizDev/ProcessBizDevBoard';
import EditProcessBizDev from '../components/processBizDev/EditProcessBizDev';
import ShareProcessButton from '../components/processBizDev/ShareProcessButton';
import ProcessBizDevListView from '../components/processBizDev/ProcessBizDevListView';
import {
  ArrowLeft,
  Users,
  Target,
  Calendar,
  TrendingUp,
  AlertCircle,
  Edit3,
  Columns3,
  Rows3
} from 'lucide-react';
import { DynamicBreadcrumb } from '../components/utils/Breadcrumb';
import Loading from '../components/utils/Loading';

const ProcessBizDevDetail = () => {
  const { processId } = useParams();
  const navigate = useNavigate();
  const API = useContext(ApiContext);
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewMode, setViewMode] = useState('kanban'); // 'kanban' ou 'list'

  const fetchProcessDetail = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(`/api/process-bizdev/${processId}`);
      setProcess(response.data);
    } catch (error) {
      console.error('Error fetching process detail:', error);
      toast.error('Erreur lors du chargement du processus');
      navigate('/prospection');
    } finally {
      setLoading(false);
    }
  }, [API, processId, navigate]);

  useEffect(() => {
    fetchProcessDetail();
  }, [fetchProcessDetail]);

  // Écouter les événements de mise à jour de candidat
  useEffect(() => {
    const handleCandidateStageUpdated = () => {
      fetchProcessDetail();
    };

    window.addEventListener('candidateStageUpdated', handleCandidateStageUpdated);

    return () => {
      window.removeEventListener('candidateStageUpdated', handleCandidateStageUpdated);
    };
  }, [fetchProcessDetail]);

  const handleBack = () => {
    navigate('/prospection');
  };

  const handleProcessUpdated = () => {
    fetchProcessDetail();
  };

  // Afficher le loading pendant le chargement
  if (loading) {
    return (
      <div className="bg-gray-100">
        <Loading message="Chargement des entreprises..." fullHeight={true} />
      </div>
    );
  }

  if (!process) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Processus introuvable</h2>
          <p className="text-gray-600 mb-4">Le processus demandé n'existe pas ou a été supprimé.</p>
          <button
            onClick={handleBack}
            className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
          >
            <ArrowLeft size={16} />
            <span>Retour aux processus</span>
          </button>
        </div>
      </div>
    );
  }

  const activeSteps = process.steps.filter(step => step.active);
  const totalProspects = process.prospects?.length || 0;
  const prospectsByStage = process.prospects?.reduce((acc, prospect) => {
    acc[prospect.currentStep] = (acc[prospect.currentStep] || 0) + 1;
    return acc;
  }, {}) || {};

  return (
    <div className="p-4 sm:p-6  lg:p-10 bg-gray-100 min-h-screen pt-16">
      
    {/* Breadcrumb */}
    <div className='pt-10'>
            <DynamicBreadcrumb 
              baseItems={[
                { label: 'Dashboard', href: '/app', icon: 'bi-house' },
                { label: 'Prospection', href: '/prospection', icon: 'bi bi-rocket-takeoff-fill' }
              ]} 
              currentItem={{ name: process.name }}
            />
    </div>

      {/* Header */}
      <div className="bg-white rounded-lg border">
        <div className="px-4 sm:px-6 py-4">
          {/* Mobile layout */}
          <div className="block md:hidden">
            <div className="flex items-center justify-between mb-4 gap-2">
              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Edit3 size={16} />
                <span className="hidden sm:inline">Modifier</span>
              </button>
              <ShareProcessButton processId={processId} processName={process.name} />
            </div>
            
            <div className="flex items-start space-x-3 mb-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Target className="text-blue-600" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-lg font-bold text-gray-900 mb-1">{process.name}</h1>
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  process.active 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {process.active ? 'Actif' : 'Inactif'}
                </span>
                {process.description && (
                  <p className="text-gray-600 text-sm mt-2">
                    {process.description}
                  </p>
                )}
              </div>
            </div>

            {/* Mobile Statistics - Grid 2x2 */}
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2">
                <Users className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                <div>
                  <div className="text-sm font-bold text-indigo-900">{totalProspects}</div>
                  <div className="text-xs text-indigo-700">prospects</div>
                </div>
              </div>
              
              <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2">
                <div>
                  <div className="text-sm font-bold text-indigo-900">{activeSteps.length}</div>
                  <div className="text-xs text-indigo-700">étapes</div>
                </div>
              </div>
              
              <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2">
                <TrendingUp className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                <div>
                  <div className="text-sm font-bold text-indigo-900">{prospectsByStage['deal'] || 0}</div>
                  <div className="text-xs text-indigo-700">conversions</div>
                </div>
              </div>
              
              <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-2">
                <Calendar className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                <div>
                  <div className="text-xs text-indigo-700">créé le</div>
                  <div className="text-xs font-bold text-indigo-900">
                    {new Date(process.createdAt).toLocaleDateString('fr-FR')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Desktop layout */}
          <div className="hidden md:flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className=" border-gray-300 pl-4">
                <div className="flex items-center space-x-3">
                  
                  <div>
                    <div className='flex items-center space-x-2'>
                      <div className="bg-gray-50 p-2 border rounded-lg">                      
                        <Target className="text-red-600" size={24} />
                      </div>
                      <h1 className="text-xl font-bold text-gray-900 mb-0">{process.name}</h1>
                    </div>
                    

                    {/* Desktop Statistics */}
                    <div className="flex flex-wrap gap-2 sm:gap-3 mt-2">
                      <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                        <Users className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                        <div className="flex items-center space-x-1">
                          <span className="text-xs font-bold text-indigo-700">{totalProspects}</span>
                          <span className="text-xs text-indigo-700">prospects</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                        <div className="flex items-center space-x-1">
                          <span className="text-xs font-bold text-indigo-700">{activeSteps.length}</span>
                          <span className="text-xs text-indigo-700">étapes actives</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                        <TrendingUp className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                        <div className="flex items-center space-x-1">
                          <span className="text-xs font-bold text-indigo-700">{prospectsByStage['deal'] || 0}</span>
                          <span className="text-xs text-indigo-700">conversions</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center bg-indigo-50 rounded-lg border border-indigo-200 px-2 py-1 min-w-0">
                        <Calendar className="text-indigo-600 mr-2 flex-shrink-0" size={14} />
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-indigo-700">créé le</span>
                          <span className="text-xs font-bold text-indigo-700">
                            {new Date(process.createdAt).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-600 text-sm mt-2">
                      {process.description || 'Aucune description'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                process.active
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {process.active ? 'Actif' : 'Inactif'}
              </span>

              <ShareProcessButton processId={processId} processName={process.name} />

              <button
                onClick={() => setShowEditModal(true)}
                className="flex items-center space-x-2 px-3 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <Edit3 size={16} />
                <span>Modifier</span>
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* Switch de vue */}
      {activeSteps.length > 0 && (
        <div className="flex justify-left py-2">
          <div className="inline-flex rounded-lg border border-gray-300 bg-white">
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-2 py-1 rounded-l-md text-sm font-medium transition-colors
                ${viewMode === 'kanban' ? ' text-gray-700 hover:text-gray-900' : 'bg-gray-100 text-gray-700 hover:text-gray-900'}`}
            >
              <Columns3 size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-2 py-1 rounded-r-md text-sm font-medium transition-colors
                ${viewMode === 'list' ? ' text-gray-700 hover:text-gray-900' : 'bg-gray-100 text-gray-700 hover:text-gray-900'}`}
            >
              <Rows3 size={20} />
            </button>
          </div>
        </div>
      )}

      {/* Tableau Kanban ou Liste */}
      <div className="flex-1">
        {activeSteps.length === 0 ? (
          <div className="p-12 text-center bg-white m-6 rounded-lg border">
            <Target className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune étape active
            </h3>
            <p className="text-gray-600 mb-4">
              Activez au moins une étape pour afficher le tableau Kanban.
            </p>
            <button
              onClick={() => setShowEditModal(true)}
              className="inline-flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
            >
              <Edit3 size={16} />
              <span>Modifier le processus</span>
            </button>
          </div>
        ) : (
          <div className="mt-2 p-2 bg-gray-50 rounded-lg shadow-sm">
            <ProcessBizDevBoard processId={processId} viewMode={viewMode} />
          </div>
        )}
      </div>

      {/* Footer avec actions rapides */}
      <div className="bg-white border-t px-6 py-4 mt-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4 text-sm text-gray-500">
            <span>Dernière mise à jour : {new Date(process.updatedAt).toLocaleString('fr-FR')}</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500 mr-2">Actions rapides :</span>
            <button
              onClick={() => setShowEditModal(true)}
              className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded"
            >
              Modifier le processus
            </button>
          </div>
        </div>
      </div>

      {/* Modal d'édition */}
      <EditProcessBizDev
        process={process}
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onProcessUpdated={handleProcessUpdated}
      />
    </div>
  );
};

export default ProcessBizDevDetail;